import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/services', label: 'Services' },
    { to: '/technology', label: 'Technology' },
    { to: '/industries', label: 'Industries' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'backdrop-blur-xl' : ''}`}
      style={{
        background: isScrolled 
          ? (isDark ? 'rgba(33, 37, 41, 0.8)' : 'rgba(240, 244, 248, 0.8)')
          : 'transparent'
      }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link">
            <Logo />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-all duration-200 hover:text-brand-primary relative ${
                  location.pathname === link.to
                    ? 'text-brand-primary'
                    : isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
                {location.pathname === link.to && (
                  <motion.div
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-brand-primary"
                    layoutId="navbar-indicator"
                  />
                )}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <ThemeToggle />
            <Link
              to="/contact"
              className={`px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0 bg-brand-primary hover:bg-brand-primary-hover text-white ${
                isDark ? 'shadow-neu-dark-sm hover:shadow-neu-dark' : 'shadow-neu-light-sm hover:shadow-neu-light'
              }`}
              data-testid="cta-button-nav"
            >
              Get Started
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-3">
            <ThemeToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-xl transition-all ${
                isDark 
                  ? 'text-dark-text shadow-neu-dark-sm active:shadow-neu-dark-pressed' 
                  : 'text-light-text shadow-neu-light-sm active:shadow-neu-light-pressed'
              }`}
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`lg:hidden ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block py-3 px-4 text-base font-medium rounded-xl transition-all ${
                    location.pathname === link.to
                      ? `text-brand-primary ${isDark ? 'shadow-neu-dark-pressed' : 'shadow-neu-light-pressed'}`
                      : isDark 
                        ? 'text-dark-text-secondary hover:text-brand-primary' 
                        : 'text-light-text-secondary hover:text-brand-primary'
                  }`}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className={`block w-full bg-brand-primary hover:bg-brand-primary-hover text-white px-6 py-3 rounded-full text-center text-sm font-semibold transition-all mt-4 ${
                  isDark ? 'shadow-neu-dark-sm' : 'shadow-neu-light-sm'
                }`}
                data-testid="cta-button-mobile"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;